import React, { Fragment, useState } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { ChevronDown, ChevronRight, ListOrdered, UserPlus } from 'lucide-react';
import { useTenants } from '../../hooks/tenants/useTenants';
import { useSpotQueueCandidates } from '../../hooks/parkingQueue';
import { describeWaited, groupLabel, QueueRowFacts, SpotRef } from '../../Helpers/parkingQueueUtils';
import { ParkingQueueEntry } from '../../types/parkingQueue';
import { TenantKind } from '../../types/Enums';
import DeviationNotice from './DeviationNotice';
import ParkingQueueRulesBox from './ParkingQueueRulesBox';
import { internalText } from './queueLabels';
import './ParkingQueue.css';

interface Props {
    /** Lets the spot modal scroll to the section (Välj från kön). */
    sectionRef?: React.Ref<HTMLElement>;
    spotRef: SpotRef;
    /** Held by the spot modal, which stays mounted while the lease form is open. */
    expanded: boolean;
    onToggle: () => void;
    /**
     * When the spot can be rented out: `null` now, a date from the day after its
     * lease ends, `undefined` not at all (tills vidare): the list shows without Hyr ut.
     */
    rentFrom: string | null | undefined;
    /** Why the rows have no Hyr ut ("Platsen är uthyrd tills vidare."); absent while that is not known yet. */
    notRentableNote?: string;
    /** Bumped by the page after a lease from the queue, to read the list again. */
    reloadKey: number;
    /** The applicant picked; `reason` is set when someone stood ahead. */
    onPick: (entry: ParkingQueueEntry, reason?: string) => void;
    /** "Lägg till i kön": Ny köplats prefilled from the spot. */
    onAddToQueue: () => void;
    submitting: boolean;
}

/**
 * The spot modal's Kö section: collapsed by default; expanding reads the active
 * entries the spot matches, in the standard order, under "Regler för kön" when written. Hyr ut on the first in line
 * picks at once; anyone further back first needs the reason for passing over
 * those ahead.
 */
const QueueCandidatesSection: React.FC<Props> = ({ sectionRef, spotRef, expanded, onToggle, rentFrom, notRentableNote, reloadKey, onPick, onAddToQueue, submitting }) => (
    <section ref={sectionRef} className="rentable-detail-section">
        <button type="button" className="queue-section-toggle" onClick={onToggle} aria-expanded={expanded}>
            <span className="rentable-detail-section-title">
                <ListOrdered size={16} />Kö
                {rentFrom && <span className="rentable-detail-section-count">Hyr ut från {rentFrom}</span>}
            </span>
            {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        </button>
        {expanded && <QueueCandidatesList spotRef={spotRef} canRent={rentFrom !== undefined} note={rentFrom === undefined ? notRentableNote : undefined} reloadKey={reloadKey} onPick={onPick} onAddToQueue={onAddToQueue} submitting={submitting} />}
    </section>
);

interface ListProps {
    spotRef: SpotRef;
    canRent: boolean;
    note?: string;
    reloadKey: number;
    onPick: (entry: ParkingQueueEntry, reason?: string) => void;
    onAddToQueue: () => void;
    submitting: boolean;
}

/** Mounted on expand, so every expand reads the queue again. */
const QueueCandidatesList: React.FC<ListProps> = ({ spotRef, canRent, note, reloadKey, onPick, onAddToQueue, submitting }) => {
    const { rows, config, loading, error } = useSpotQueueCandidates(spotRef, reloadKey);
    const [passOverKey, setPassOverKey] = useState<string | null>(null);
    const [reason, setReason] = useState('');
    const [reasonError, setReasonError] = useState<string | undefined>(undefined);

    const rentOut = (row: QueueRowFacts, index: number) => {
        if (index === 0) {
            onPick(row.entry);
            return;
        }
        if (passOverKey !== row.entry.key) {
            setPassOverKey(row.entry.key);
            setReason('');
            setReasonError(undefined);
        }
    };

    const confirmPassOver = (row: QueueRowFacts) => {
        if (!reason.trim()) {
            setReasonError('Ange anledning');
            return;
        }
        onPick(row.entry, reason.trim());
    };

    let lastGroup: string | null = null;
    return (
        <div className="queue-section-body">
            {note && <p className="text-muted small mb-2">{note}</p>}
            {error && <Alert variant="danger">{error}</Alert>}
            <ParkingQueueRulesBox readOnly />
            {loading ? (
                <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
            ) : !error && rows.length === 0 ? (
                <p className="text-muted mb-2">Ingen i kön passar platsen.</p>
            ) : (
                <ol className="queue-picker-list">
                    {rows.map((row, index) => {
                        const group = groupLabel(row, config);
                        const header = group !== null && group !== lastGroup ? group : null;
                        lastGroup = group;
                        return (
                            <Fragment key={row.entry.key}>
                                {header && <li className="queue-picker-group" aria-hidden>{header}</li>}
                                <QueueCandidateRow row={row} position={index + 1} expanded={passOverKey === row.entry.key}
                                    onRentOut={canRent ? () => rentOut(row, index) : undefined} submitting={submitting}>
                                    <DeviationNotice ahead={rows.slice(0, index).map(r => r.entry)} reason={reason}
                                        onReasonChange={value => { setReason(value); setReasonError(undefined); }}
                                        error={reasonError} idPrefix="queue-section" />
                                    <div className="d-flex gap-2 justify-content-end">
                                        <Button variant="secondary" size="sm" onClick={() => setPassOverKey(null)}>Avbryt</Button>
                                        <Button variant="primary" size="sm" onClick={() => confirmPassOver(row)} disabled={submitting}>Fortsätt</Button>
                                    </div>
                                </QueueCandidateRow>
                            </Fragment>
                        );
                    })}
                </ol>
            )}
            <div className="rentable-detail-links">
                <button type="button" className="rentable-detail-link" onClick={onAddToQueue} disabled={submitting}>
                    <UserPlus size={14} />Lägg till i kön
                </button>
            </div>
        </div>
    );
};

interface RowProps {
    row: QueueRowFacts;
    position: number;
    /** The pass-over step is shown. */
    expanded: boolean;
    /** Absent while the spot cannot be rented out. */
    onRentOut?: () => void;
    submitting: boolean;
    /** The pass-over step. */
    children: React.ReactNode;
}

const QueueCandidateRow: React.FC<RowProps> = ({ row, position, expanded, onRentOut, submitting, children }) => {
    const { tenantsByKey, getTenantName } = useTenants();
    const { entry } = row;
    const kind = tenantsByKey[entry.tenantKey]?.kind === TenantKind.Company ? 'Företag' : 'Person';
    const held = row.heldSpots.length;
    return (
        <li className={`queue-picker-item${expanded ? ' is-expanded' : ''}`}>
            <div className="queue-picker-row">
                <span className="queue-picker-position">{position}</span>
                <span className="queue-picker-main">
                    <span className="queue-picker-name">{getTenantName(entry.tenantKey)} <span className="text-muted small">{kind}</span></span>
                    <span className="queue-picker-facts" title={row.heldSpots.map(s => s.label).join(', ') || undefined}>
                        {internalText(row)} · {held} {held === 1 ? 'plats' : 'platser'} idag
                    </span>
                    <span className="queue-picker-facts">Ködatum {entry.queueDate} · {describeWaited(row.daysWaited)}</span>
                    {row.latestNote && <span className="queue-picker-note">{row.latestNote}</span>}
                </span>
                {onRentOut && (
                    <Button variant="outline-primary" size="sm" className="queue-picker-rent" onClick={onRentOut}
                        disabled={submitting} aria-expanded={position > 1 ? expanded : undefined}>
                        Hyr ut
                    </Button>
                )}
            </div>
            {expanded && <div className="queue-picker-passover">{children}</div>}
        </li>
    );
};

export default QueueCandidatesSection;
